"use client";

import type * as React from "react";

import { Blocks } from "@/types/cms";
import SectionHeader from "@/components/sections/utils/SectionHeader";
import { Separator } from "@/components/separator/separator";
import { getSectionSpacingClasses, normalizeSpace } from "@/components/sections/utils/spacing";
import CartForm, { CartCopy } from "./CartForm";
import styles from "./CartSection.module.css";

/**
 * Cart Section (`section.cart`) — the /cart page. The gift stored in the
 * gift-cart cookie renders as a product row from its cookie snapshot (no
 * CMS cards here), followed by the purchase form; every form string
 * is CMS-editable via the block's Form Copy tab. Header, alignment, width,
 * spacing, and divider behave like a card section's. No bleed — the cart
 * is a focused column.
 */

interface CartSectionData {
  title?: string | null;
  eyebrow?: string | null;
  icon?: string | null;
  content?: Blocks;
  textJustify?: "left" | "center" | "right" | null;
  contentAlign?: "left" | "center" | "right" | null;
  headerWidth?: "sm" | "md" | "lg" | null;
  divider?: boolean | null;
  sectionWidth?: string | null;
  spaceTop?: string | null;
  spaceBottom?: string | null;
  emptyCartText?: string | null;
  emptyCartLinkLabel?: string | null;
  formTitle?: string | null;
  recipientEmailLabel?: string | null;
  recipientEmailPlaceholder?: string | null;
  recipientEmailHelper?: string | null;
  confirmEmailLabel?: string | null;
  confirmEmailPlaceholder?: string | null;
  confirmEmailHelper?: string | null;
  senderNameLabel?: string | null;
  senderNamePlaceholder?: string | null;
  senderNameHelper?: string | null;
  messageLabel?: string | null;
  messagePlaceholder?: string | null;
  messageHelper?: string | null;
  buyerEmailLabel?: string | null;
  buyerEmailPlaceholder?: string | null;
  buyerEmailHelper?: string | null;
  submitLabel?: string | null;
  submitBusyLabel?: string | null;
  finePrint?: string | null;
}

interface CartSectionProps {
  data: CartSectionData;
}

const CartSection = ({ data }: CartSectionProps) => {
  if (!data) return null;

  const showDivider = data.divider === true;
  // Divider-above-space: same idiom as CardSection/GiftFormSection.
  const spaceTopToken = normalizeSpace(data.spaceTop ?? undefined);
  const spaceBottomToken = normalizeSpace(data.spaceBottom ?? undefined);
  const sectionSpacingClass = showDivider
    ? (spaceBottomToken ? `mb-${spaceBottomToken}` : "")
    : getSectionSpacingClasses({
        spaceTop: data.spaceTop ?? undefined,
        spaceBottom: data.spaceBottom ?? undefined,
      });
  const dividerGapClass = spaceTopToken
    ? (styles as Record<string, string>)[
        `dividerGap${spaceTopToken[0].toUpperCase()}${spaceTopToken.slice(1)}`
      ]
    : "";
  const dividerClass = [styles.divider, dividerGapClass].filter(Boolean).join(" ");

  const widthClass = data.sectionWidth ? `w-${data.sectionWidth}` : undefined;
  const sectionClass = ["section", sectionSpacingClass].filter(Boolean).join(" ");
  const containerClass = ["section-container", widthClass].filter(Boolean).join(" ");

  const copy: CartCopy = {
    emptyCartText: data.emptyCartText,
    emptyCartLinkLabel: data.emptyCartLinkLabel,
    formTitle: data.formTitle,
    recipientEmailLabel: data.recipientEmailLabel,
    recipientEmailPlaceholder: data.recipientEmailPlaceholder,
    recipientEmailHelper: data.recipientEmailHelper,
    confirmEmailLabel: data.confirmEmailLabel,
    confirmEmailPlaceholder: data.confirmEmailPlaceholder,
    confirmEmailHelper: data.confirmEmailHelper,
    senderNameLabel: data.senderNameLabel,
    senderNamePlaceholder: data.senderNamePlaceholder,
    senderNameHelper: data.senderNameHelper,
    messageLabel: data.messageLabel,
    messagePlaceholder: data.messagePlaceholder,
    messageHelper: data.messageHelper,
    buyerEmailLabel: data.buyerEmailLabel,
    buyerEmailPlaceholder: data.buyerEmailPlaceholder,
    buyerEmailHelper: data.buyerEmailHelper,
    submitLabel: data.submitLabel,
    submitBusyLabel: data.submitBusyLabel,
    finePrint: data.finePrint,
  };

  return (
    <section id="cart" className={sectionClass}>
      <div className={containerClass}>
        {showDivider && (
          <div className={dividerClass}>
            <Separator />
          </div>
        )}
        <SectionHeader
          content={data.content}
          align={data.contentAlign ?? undefined}
          textAlign={data.textJustify ?? undefined}
          headerWidth={data.headerWidth ?? undefined}
          eyebrow={data.eyebrow ?? undefined}
          icon={data.icon ?? undefined}
        />
        <div className="reveal-item" style={{ "--reveal-index": 1 } as React.CSSProperties}>
          <CartForm copy={copy} />
        </div>
      </div>
    </section>
  );
};

export default CartSection;
